/**
 * Console Ingest now control (DCL-13 steps 2 to 4, founder ruling DP-DCL-14).
 *
 * Starts the product ingest job and shows that run's progress and report. The SPA
 * renders the API answer and never decides what was ingested or refused.
 */
import React from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  TooltipHost,
} from "@fluentui/react";
import { ArrowSyncCircleRegular } from "@fluentui/react-icons";
import { useEnterKeyModalDefaultAction } from "../../hooks/useEnterKeyModalDefaultAction";
import {
  fetchConsoleIngestRun,
  startConsoleIngest,
  type TokenSource,
} from "../../api/consoleApi";
import type {
  ConsoleIngestDecision,
  ConsoleIngestResponse,
  ConsoleRefusal,
} from "../../api/types";
import styles from "./IngestNowControl.module.css";

const POLL_MS = 2500;
const MESSAGE_INGEST_DID_NOT_FINISH = "Ingest did not finish, so nothing was added.";

function isIngestAnswer(
  body: ConsoleIngestResponse | ConsoleRefusal,
): body is ConsoleIngestResponse {
  return (body as ConsoleIngestResponse).response_type === "console_ingest_run";
}

function refusedDecisions(answer: ConsoleIngestResponse): ConsoleIngestDecision[] {
  return (answer.report?.decisions || []).filter((row) =>
    String(row.decision || "").startsWith("refused_"),
  );
}

function runIsFinished(answer: ConsoleIngestResponse): boolean {
  const status = String(answer.run?.status || "");
  return status === "completed" || Boolean(answer.report) || answer.outcome_type !== "success";
}

function countFromReport(answer: ConsoleIngestResponse, decision: string): number {
  const fromCounts = answer.report?.counts?.[decision];
  if (typeof fromCounts === "number") {
    return fromCounts;
  }
  return (answer.report?.decisions || []).filter((row) => row.decision === decision).length;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

export function formatIngestedAt(iso?: string | null): string {
  const parsed = iso ? new Date(iso) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} @ ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function fileCountSentence(count: number, kind: "added" | "updated"): string {
  switch (kind) {
    case "added":
      if (count === 0) {
        return "There were no new files added.";
      }
      if (count === 1) {
        return "There was 1 new file added.";
      }
      return `There were ${count} new files added.`;
    case "updated":
      if (count === 0) {
        return "There were no new files updated.";
      }
      if (count === 1) {
        return "There was 1 new file updated.";
      }
      return `There were ${count} new files updated.`;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function completeSummary(answer: ConsoleIngestResponse | null): string {
  const added = answer ? countFromReport(answer, "ingested") : 0;
  const edited = answer ? countFromReport(answer, "reingested") : 0;
  const when = formatIngestedAt(answer?.run?.updated_at);
  return [
    `The knowledge base was successfully ingested on ${when}.`,
    fileCountSentence(added, "added"),
    fileCountSentence(edited, "updated"),
  ].join("\n\n");
}

export type IngestNowControlProps = {
  getToken: TokenSource;
};

export function IngestNowControl({ getToken }: IngestNowControlProps) {
  const [busy, setBusy] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [answer, setAnswer] = React.useState<ConsoleIngestResponse | ConsoleRefusal | null>(
    null,
  );
  const inFlight = React.useRef(false);

  const ingestAnswer = answer && isIngestAnswer(answer) ? answer : null;
  const refusalMessage =
    answer && !isIngestAnswer(answer) ? answer.message : ingestAnswer?.message;
  const runId = ingestAnswer?.run?.id;
  const polling = Boolean(busy && ingestAnswer && runId && !runIsFinished(ingestAnswer));
  const dialogTitle = "Knowledge pack ingestion";
  const runFinished = Boolean(ingestAnswer?.run || ingestAnswer?.report);
  const dialogBody = runFinished
    ? completeSummary(ingestAnswer)
    : refusalMessage || MESSAGE_INGEST_DID_NOT_FINISH;
  const refused = ingestAnswer ? refusedDecisions(ingestAnswer) : [];

  const finish = React.useCallback((next: ConsoleIngestResponse | ConsoleRefusal) => {
    inFlight.current = false;
    setBusy(false);
    setAnswer(next);
    setDialogOpen(true);
  }, []);

  React.useEffect(() => {
    if (!polling || runId === undefined) {
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetchConsoleIngestRun(getToken, runId)
        .then((result) => {
          if (cancelled) {
            return;
          }
          setAnswer(result.body);
          if (!isIngestAnswer(result.body) || runIsFinished(result.body)) {
            finish(result.body);
          }
        })
        .catch(() => {
          if (!cancelled) {
            finish({
              response_type: "console_ingest_run",
              outcome_type: "safe_failure",
              routing: "CC1-CHAIN",
              message: MESSAGE_INGEST_DID_NOT_FINISH,
              run: ingestAnswer?.run,
              report: null,
            });
          }
        });
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [getToken, polling, runId, ingestAnswer, finish]);

  const onClick = () => {
    if (inFlight.current || busy) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setAnswer(null);
    setDialogOpen(false);
    void startConsoleIngest(getToken)
      .then((result) => {
        setAnswer(result.body);
        if (!isIngestAnswer(result.body) || runIsFinished(result.body) || !result.body.run?.id) {
          finish(result.body);
        }
      })
      .catch(() => {
        finish({
          response_type: "console_ingest_run",
          outcome_type: "safe_failure",
          routing: "CC1-CHAIN",
          message: MESSAGE_INGEST_DID_NOT_FINISH,
          run: null,
          report: null,
        });
      });
  };

  const closeDialog = () => setDialogOpen(false);
  useEnterKeyModalDefaultAction(dialogOpen, closeDialog);

  const dialogContentProps = {
    type: DialogType.normal,
    title: dialogTitle,
    closeButtonAriaLabel: "Close",
  };

  return (
    <div className={styles.root}>
      <TooltipHost content={busy ? "Ingesting knowledge" : "Ingest knowledge"}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onClick}
          disabled={busy}
          aria-label="Ingest knowledge"
          aria-busy={busy}
          data-testid="console-ingest-now"
        >
          <ArrowSyncCircleRegular
            className={`${styles.icon} ${busy ? styles.iconSpinning : ""}`}
          />
        </button>
      </TooltipHost>
      {typeof document !== "undefined"
        ? createPortal(
            <Dialog
              hidden={!dialogOpen}
              onDismiss={closeDialog}
              dialogContentProps={dialogContentProps}
              modalProps={{ isBlocking: true }}
            >
              {dialogBody ? (
                <p data-testid="console-ingest-message" className={styles.message}>
                  {dialogBody}
                </p>
              ) : null}
              {refused.length > 0 ? (
                <ul data-testid="console-ingest-refused">
                  {refused.map((row) => (
                    <li key={row.path}>
                      {row.path}: {row.decision}
                      {row.reason ? ` - ${row.reason}` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
              <DialogFooter>
                <PrimaryButton onClick={closeDialog} text="OK" />
              </DialogFooter>
            </Dialog>,
            document.body,
          )
        : null}
    </div>
  );
}
